import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, HashRouter, Routes, Route } from "react-router-dom";
import Layout from "./Pages/Layout";
import Calendar from "./Pages/Calendar";
import LogIn from "./Pages/LogIn";
import OAuthCallback from "./Pages/OAuthCallback";
import Agenda from "./Pages/Agenda";
import Todo from "./Pages/Todo";
import Settings from "./Pages/Settings";
import Resources from "./Pages/Resources";
import NewWork from "./Pages/NewWork";
import PlanMission from "./Components/Pilot/PlanMission";
import OnLocation from "./Components/Pilot/OnLocation";
import Operations from "./Components/Pilot/Operations";
import PostFlight from "./Components/Pilot/PostFlight";
import Update from "./Pages/Update";
import DataAnalysis from './Pages/DataAnalysis';
import "./index.css";
import "./Pages/Layout.css";
import "./styles/electron.css";
import { ProtectedRoute } from "./Components/ProtectedRoutes";
import { AuthProvider, useAuth } from "./Hooks/AuthProvider.jsx";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { environment } from "./config/environment";
import { LiveblocksProvider } from "@liveblocks/react";
import { functions, httpsCallable } from "./Utils/firebaseConfig";
import NotificationsProvider from "./Components/NotificationsProvider.jsx";
import GoogleSignInCornerLoader from "./Components/GoogleSignInCornerLoader.jsx";
import AppUpdateBanner from "./Components/AppUpdateBanner.jsx";
import { useNotifications } from "./Components/NotificationsProvider.jsx";
import { getQueue, setQueue, removeItem } from "./Utils/offlineQueue";


/**
 * This new component waits for a user to be authenticated before it
 * creates the Liveblocks client. This solves the timing issue.
 */
// Component to handle Electron app ready signal when auth is complete
const ElectronReadyHandler = ({ children }) => {
  const { loading, user, handleLogout, revokeAccess, refreshUserSettings, connectGoogleCalendar, setRentmanSyncEnabled, kickCalendarSync } = useAuth();
  const revokeInFlightRef = useRef(false);

  const sendIpc = (sendFn, result) => {
    if (typeof sendFn === "function") {
      sendFn(result);
    }
  };

  // Add Electron-specific class to body
  useEffect(() => {
    if (environment.platform === 'electron') {
      // Add electron-app class to body for CSS targeting
      document.body.classList.add('electron-app');
      console.log('[Electron CSS] Added electron-app class to body');
      
      // Cleanup on unmount
      return () => {
        document.body.classList.remove('electron-app');
      };
    }
  }, []);

  useEffect(() => {
    console.log('[App] Auth state changed - loading:', loading, 'user:', user ? 'present' : 'none');
    console.log('[App] Environment platform:', environment.platform);
    console.log('[App] electronAPI available:', !!window.electronAPI);
    console.log('[App] appReady available:', !!window.electronAPI?.appReady);
    
    if (environment.platform === 'electron' && window.electronAPI?.appReady && !loading) {
      console.log('[App] Auth loading complete, signaling app ready to Electron');
      window.electronAPI.appReady()
        .then(() => console.log('[App] App ready signal sent successfully'))
        .catch(error => console.error('[App] Failed to send app ready signal:', error));
    }
  }, [loading, user]);

  useEffect(() => {
    if (environment.platform !== 'electron' || !window.electronAPI?.onSignOutRequest) {
      return undefined;
    }

    const handleSignOutRequest = () => {
      console.log('[App] Sign out requested from settings window');
      handleLogout();
    };

    const unsubscribe = window.electronAPI.onSignOutRequest(handleSignOutRequest);
    return unsubscribe;
  }, [handleLogout]);

  useEffect(() => {
    if (environment.platform !== 'electron' || !window.electronAPI?.onRevokeGoogleCalendarRequest) {
      return undefined;
    }

    const handleRevokeRequest = async () => {
      if (revokeInFlightRef.current) {
        console.warn('[App] Revoke Google Calendar already in progress, ignoring duplicate request');
        sendIpc(window.electronAPI?.sendRevokeGoogleCalendarResult, {
          success: false,
          error: "Disconnect already in progress",
        });
        return;
      }
      revokeInFlightRef.current = true;
      try {
        await revokeAccess();
        if (refreshUserSettings) {
          await refreshUserSettings();
        }
        sendIpc(window.electronAPI?.sendRevokeGoogleCalendarResult, { success: true });
      } catch (error) {
        console.error('[App] Revoke Google Calendar failed:', error);
        sendIpc(window.electronAPI?.sendRevokeGoogleCalendarResult, {
          success: false,
          error: error?.message || 'Failed to revoke Google Calendar access',
        });
      } finally {
        revokeInFlightRef.current = false;
      }
    };

    const unsubscribe = window.electronAPI.onRevokeGoogleCalendarRequest(handleRevokeRequest);
    return unsubscribe;
  }, [revokeAccess, refreshUserSettings]);

  useEffect(() => {
    if (environment.platform !== 'electron' || !window.electronAPI?.onConnectGoogleCalendarRequest) {
      return undefined;
    }

    const handleConnectRequest = async () => {
      try {
        await connectGoogleCalendar();
        if (refreshUserSettings) await refreshUserSettings();
        sendIpc(window.electronAPI?.sendConnectGoogleCalendarResult, { success: true });
      } catch (error) {
        sendIpc(window.electronAPI?.sendConnectGoogleCalendarResult, {
          success: false,
          error: error?.message || 'Failed to connect Google Calendar',
        });
      }
    };

    const unsubscribe = window.electronAPI.onConnectGoogleCalendarRequest(handleConnectRequest);
    return unsubscribe;
  }, [connectGoogleCalendar, refreshUserSettings]);

  useEffect(() => {
    if (environment.platform !== 'electron' || !window.electronAPI?.onSetRentmanSyncRequest) {
      return undefined;
    }

    const handleRentmanToggle = async (data) => {
      try {
        const result = await setRentmanSyncEnabled(!!data?.enabled);
        if (refreshUserSettings) await refreshUserSettings();
        sendIpc(window.electronAPI?.sendSetRentmanSyncResult, {
          success: true,
          rentmanSyncEnabled: result?.rentmanSyncEnabled ?? !!data?.enabled,
        });
      } catch (error) {
        sendIpc(window.electronAPI?.sendSetRentmanSyncResult, {
          success: false,
          error: error?.message || 'Failed to update Rentman sync',
        });
      }
    };

    const unsubscribe = window.electronAPI.onSetRentmanSyncRequest(handleRentmanToggle);
    return unsubscribe;
  }, [setRentmanSyncEnabled, refreshUserSettings]);

  useEffect(() => {
    if (environment.platform !== 'electron' || !window.electronAPI?.onSyncCalendarNowRequest) {
      return undefined;
    }

    const handleSyncRequest = async (data) => {
      const target = data?.target || "motionAir";
      try {
        kickCalendarSync(target);
        sendIpc(window.electronAPI?.sendSyncCalendarNowResult, { success: true, started: true });
      } catch (error) {
        sendIpc(window.electronAPI?.sendSyncCalendarNowResult, {
          success: false,
          error: error?.message || 'Failed to start calendar sync',
        });
      }
    };

    const unsubscribe = window.electronAPI.onSyncCalendarNowRequest(handleSyncRequest);
    return unsubscribe;
  }, [kickCalendarSync]);

  // Clear any active verifier listeners on logout/auth change by unmounting/reinitializing children tree
  return <>{children}</>;
};

const LiveblocksClientProvider = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <>{children}</>;
  }

  // Keep LiveblocksProvider mounted even when offline to avoid context errors in RoomProvider

  const functionName = 'liveblocksAuth';
  const authFunction = httpsCallable(functions, functionName);

  const authEndpoint = async (room) => {
    try {
      const { data } = await authFunction({ room });
      return data;
    } catch (error) {
      console.error("Liveblocks authentication failed:", error);
      return { error: "forbidden", reason: error.message || "Authentication failed." };
    }
  };

  return <LiveblocksProvider authEndpoint={authEndpoint}>{children}</LiveblocksProvider>;
};

const AppRoutes = () => (
    <Routes>
      <Route path="/" element={<Layout hideHeaderPaths={["/"]} />}>
        <Route index element={<LogIn />} />
        <Route path="calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
        <Route path="agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
        <Route path="todo" element={<ProtectedRoute><Todo /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
        <Route path="resources/new-work" element={<ProtectedRoute><NewWork /></ProtectedRoute>} />
        <Route path="pilot" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
        <Route path="pilot/plan" element={<ProtectedRoute><PlanMission /></ProtectedRoute>} />
        <Route path="pilot/location" element={<ProtectedRoute><OnLocation /></ProtectedRoute>} />
        <Route path="pilot/operations" element={<ProtectedRoute><Operations /></ProtectedRoute>} />
        <Route path="pilot/post" element={<ProtectedRoute><PostFlight /></ProtectedRoute>} />
        <Route path="data-analysis" element={<ProtectedRoute><DataAnalysis /></ProtectedRoute>} />
      </Route>
      <Route path="/oauth2callback" element={<OAuthCallback />} />
      <Route path="/update" element={<Update />} />
    </Routes>
);

const App = () => {
  const theme = createTheme({
    palette: { primary: { main: "#3498B7" }, secondary: { main: "#2E2E2E" } },
    typography: { fontFamily: [ "Montserrat", "sans-serif", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "Roboto", '"Helvetica Neue"', "Arial", "sans-serif", '"Apple Color Emoji"', '"Segoe UI Emoji"', '"Segoe UI Symbol"', ].join(","), },
    breakpoints: { values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920, }, },
  });

  const RouterComponent = environment.platform === 'electron' ? HashRouter : BrowserRouter;

  return (
    <RouterComponent>
      <AuthProvider>
        <ElectronReadyHandler>
          <NotificationsProvider>
            <LiveblocksClientProvider>
              <ThemeProvider theme={theme}>
                <OfflineQueueBridge />
                <QueueSyncVerifier />
                <OfflineBadge />
                <GoogleSignInCornerLoader />
                <AppUpdateBanner />
                <AppRoutes />
              </ThemeProvider>
            </LiveblocksClientProvider>
          </NotificationsProvider>
        </ElectronReadyHandler>
      </AuthProvider>
    </RouterComponent>
  );
};

export default App;

// Small badge to indicate offline status
const OfflineBadge = () => {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div style={{
      position: 'fixed',
      right: 12,
      bottom: 12,
      background: 'rgba(200, 30, 30, 0.95)',
      color: '#fff',
      padding: '6px 10px',
      borderRadius: 8,
      fontSize: 12,
      zIndex: 9999,
      boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
    }}>
      Offline
    </div>
  );
};

// Headless bridge: turn offline queue items into notifications for the bell
/**
 * Bridges the offline queue (localStorage) into ephemeral notifications.
 * De-dupes by queue key and persists a seen set so reloads don’t duplicate.
 */
const OfflineQueueBridge = () => {
  const { add } = useNotifications() || {};
  const [tick, setTick] = useState(0);
  const lastCountRef = React.useRef(0);
  const seenKeysRef = React.useRef(new Set());
  const SEEN_KEYS_STORAGE = 'offlineQueueSeenKeys';

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1500);
    return () => clearInterval(id);
  }, []);

  // Bootstrap seen keys on mount so we don't re-notify pre-existing items after reload
  useEffect(() => {
    try {
      const persisted = JSON.parse(localStorage.getItem(SEEN_KEYS_STORAGE) || '[]');
      if (Array.isArray(persisted)) {
        persisted.forEach((k) => seenKeysRef.current.add(k));
      }
      // Also treat currently queued items as already seen at startup
      const q = getQueue();
      if (Array.isArray(q)) {
        for (const it of q) {
          seenKeysRef.current.add(it.key);
        }
      }
      localStorage.setItem(SEEN_KEYS_STORAGE, JSON.stringify(Array.from(seenKeysRef.current)));
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!add) return;
    const q = getQueue();
    if (q && q.length) {
      for (const it of q) {
        if (seenKeysRef.current.has(it.key)) continue;
        seenKeysRef.current.add(it.key);
        try {
          localStorage.setItem(SEEN_KEYS_STORAGE, JSON.stringify(Array.from(seenKeysRef.current)));
        } catch (_) {}
        add({
          id: `queue-${it.key}`,
          title: `${it.type} ${it.entity || 'item'}`,
          message: it.title || it.id,
          queueKey: it.key,
          queueItem: it,
        });
      }
    }
    lastCountRef.current = q ? q.length : 0;
  }, [tick, add]);

  // Polling verifier disabled to avoid races; rely on QueueSyncVerifier only.

  return null;
};

// Verified sync using short-lived snapshot listeners (least spammy, server-ack driven)
/**
 * Verified sync using short-lived snapshot listeners.
 * Subscribes per queued item and marks as "synced" only after server ack.
 */
const QueueSyncVerifier = () => {
  const { update } = useNotifications() || {};
  const listenersRef = React.useRef(new Map()); // key -> unsubscribe
  const scanRef = React.useRef(false);

  const startListener = React.useCallback(async (item) => {
    try {
      const { collection, query, where, onSnapshot } = await import('firebase/firestore');
      const { database } = await import('./Utils/firebaseConfig');
      const q = query(collection(database, item.collection), where('id', '==', item.id));
      const unsub = onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
        try {
          const fromServer = snap.metadata && snap.metadata.fromCache === false;
          if (!fromServer) return; // wait for server confirmation
          let isSynced = false;
          if (item.type === 'delete') {
            isSynced = snap.empty;
          } else {
            // create/update: any doc present from server counts as synced
            isSynced = !snap.empty && snap.docs[0] && snap.docs[0].metadata && snap.docs[0].metadata.hasPendingWrites === false;
          }
          if (isSynced) {
            const notifId = `queue-${item.key}`;
            update?.(notifId, { queueItem: { ...item, status: 'synced' } });

            // If this item is a delete and it's now confirmed by server,
            // remove any earlier queued ops for the same entity/id
            if (item.type === 'delete' && item.entity && item.id) {
              try {
                const all = getQueue();
                const toDrop = all.filter((qit) => qit.key !== item.key && qit.entity === item.entity && qit.id === item.id);
                if (toDrop.length) {
                  const pruned = all.filter((qit) => !(qit.entity === item.entity && qit.id === item.id && qit.key !== item.key));
                  setQueue(pruned);
                  // Mark older ops as resolved (synced) in notifications instead of removing them
                  toDrop.forEach((d) => {
                    const nId = `queue-${d.key}`;
                    update?.(nId, { queueItem: { ...d, status: 'synced', supersededBy: item.key } });
                    const u2 = listenersRef.current.get(d.key);
                    if (u2) { try { u2(); } catch (_) {} }
                    listenersRef.current.delete(d.key);
                  });
                }
              } catch (_) {}
            }

            removeItem(item.key);
            const u = listenersRef.current.get(item.key);
            if (u) { try { u(); } catch (_) {} }
            listenersRef.current.delete(item.key);
          }
        } catch (_) {}
      }, (err) => {
        // On error, drop listener; verifier may restart later
        const u = listenersRef.current.get(item.key);
        if (u) { try { u(); } catch (_) {} }
        listenersRef.current.delete(item.key);
      });
      listenersRef.current.set(item.key, unsub);
    } catch (_) {}
  }, [update]);

  React.useEffect(() => {
    let mounted = true;
    const scan = async () => {
      if (!mounted || scanRef.current) return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      scanRef.current = true;
      try {
        const q = getQueue();
        if (q && q.length) {
          for (const it of q) {
            if (!listenersRef.current.has(it.key)) {
              await startListener(it);
            }
          }
        }
      } finally {
        scanRef.current = false;
      }
    };

    const id = setInterval(scan, 3000);
    // initial
    void scan();
    const listeners = listenersRef.current;
    return () => {
      mounted = false;
      clearInterval(id);
      listeners.forEach((u) => { try { u(); } catch (_) {} });
      listeners.clear();
    };
  }, [startListener]);

  return null;
};